import { useCallback, useState } from "react";
import type { RefObject } from "react";

import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

import type { Map as MapInstance } from "maplibre-gl";

import { createLogger } from "../../utils/logger";

const logger = createLogger("useMapSnapshot");

type Padding = {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
};

export type MapSnapshotOverlayArgs = {
  ctx: CanvasRenderingContext2D;
  mapCanvas: HTMLCanvasElement;
  targetCanvas: HTMLCanvasElement;
  deviceScale: number;
  padding: Required<Padding>;
};

type Options = {
  mapRef: RefObject<MapInstance | null>;
  getFileName: () => string;
  getErrorMessage: (error: unknown) => string;
  fallbackError: string;
  blobErrorMessage?: string;
  padding?: Padding;
  overlay?: (args: MapSnapshotOverlayArgs) => void | Promise<void>;
};

export function useMapSnapshot({
  mapRef,
  getFileName,
  getErrorMessage,
  fallbackError,
  blobErrorMessage,
  padding,
  overlay,
}: Options) {
  const [isSavingSnapshot, setIsSavingSnapshot] = useState(false);
  const [snapshotError, setSnapshotError] = useState<string | null>(null);

  const saveSnapshot = useCallback(async () => {
    if (!mapRef.current) {
      setSnapshotError(fallbackError);
      return;
    }

    setIsSavingSnapshot(true);
    setSnapshotError(null);

    try {
      const mapCanvas = mapRef.current.getCanvas();
      const deviceScale = window.devicePixelRatio || 1;

      const paddingTop = padding?.top ?? 0;
      const paddingRight = padding?.right ?? 0;
      const paddingBottom = padding?.bottom ?? 0;
      const paddingLeft = padding?.left ?? 0;

      const targetCanvas = document.createElement("canvas");
      targetCanvas.width =
        mapCanvas.width + paddingLeft + paddingRight;
      targetCanvas.height =
        mapCanvas.height + paddingTop + paddingBottom;

      const ctx = targetCanvas.getContext("2d");
      if (!ctx) {
        throw new Error(fallbackError);
      }

      ctx.drawImage(mapCanvas, paddingLeft, paddingTop);

      if (overlay) {
        await overlay({
          ctx,
          mapCanvas,
          targetCanvas,
          deviceScale,
          padding: {
            top: paddingTop,
            right: paddingRight,
            bottom: paddingBottom,
            left: paddingLeft,
          },
        });
      }

      const blob = await new Promise<Blob | null>((resolve) =>
        targetCanvas.toBlob(resolve),
      );
      if (!blob) {
        throw new Error(blobErrorMessage ?? fallbackError);
      }

      const isNative = Capacitor.isNativePlatform?.() ?? false;
      if (isNative) {
        const readAsBase64 = () =>
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = () =>
              reject(reader.error ?? new Error(fallbackError));
            reader.onloadend = () => {
              const result = reader.result;
              if (typeof result === "string") {
                const [, payload] = result.split(",");
                resolve(payload ?? "");
              } else {
                reject(new Error(fallbackError));
              }
            };
            reader.readAsDataURL(blob);
          });
        const base64 = await readAsBase64();
        const rawFileName = getFileName();
        const fileName = rawFileName.endsWith(".png")
          ? rawFileName
          : `${rawFileName}.png`;
        const platform = Capacitor.getPlatform?.() ?? "web";
        const directory =
          platform === "android" ? Directory.External : Directory.Documents;
        let savedFile = await Filesystem.writeFile({
          path: `LotLocator/Snapshots/${fileName}`,
          data: base64,
          directory,
          recursive: true,
        }).catch(async (err) => {
          logger.warn("Snapshot save failed, falling back to cache", err);
          return Filesystem.writeFile({
            path: fileName,
            data: base64,
            directory: Directory.Cache,
            recursive: true,
          });
        });
        const fileUrl = Capacitor.convertFileSrc(savedFile.uri);
        if (typeof Share?.share === "function") {
          await Share.share({
            url: fileUrl,
            dialogTitle: fileName,
          });
        }
      } else {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        const fileName = getFileName();
        link.download = fileName.endsWith(".png") ? fileName : `${fileName}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }
    } catch (err) {
      setSnapshotError(getErrorMessage(err));
    } finally {
      setIsSavingSnapshot(false);
    }
  }, [
    blobErrorMessage,
    fallbackError,
    getErrorMessage,
    getFileName,
    mapRef,
    overlay,
    padding,
  ]);

  const clearSnapshotError = useCallback(() => setSnapshotError(null), []);

  return {
    saveSnapshot,
    isSavingSnapshot,
    snapshotError,
    clearSnapshotError,
  };
}
